import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DashValidation, isEmptyOrNil, isNameLengthValid } from 'dash-common';
import {
  FormContext,
  FormErrorTypes,
  IPriceRange,
  validatePrice,
} from 'dash-ui';
import { isNil } from 'lodash';

import { FormFields } from 'containers/properties/PropertyInventory/components/AddPropertyInventoryModal/constants/constants';

const useValidation = () => {
  const { errorHandlers } = useContext(FormContext);
  const { t } = useTranslation(['forms', 'common', 'pages']);

  const validateFieldByName = (
    fieldName: FormFields,
    value: string | IPriceRange | null
  ) => {
    const setInvalid = (message: string, args: Record<string, string> = {}) => {
      errorHandlers[fieldName]({
        type: FormErrorTypes.Invalid,
        message: t(message, args),
      });
    };

    const resetPricesErrors = () => {
      errorHandlers[FormFields.Prices](null);
    };

    const unitMap = {
      [FormFields.Bedrooms]: 'Bedrooms',
      [FormFields.Bathrooms]: 'Bathrooms',
      [FormFields.InteriorSqft]: 'Interior sqft',
      [FormFields.ExteriorSqft]: 'Exterior sqft',
    };

    // check specific fields type
    switch (fieldName) {
      case FormFields.Name: {
        if (isEmptyOrNil(value as string)) {
          setInvalid(`forms:messages.nameBlank`);
          return false;
        }
        if (!isNameLengthValid(value as string)) {
          setInvalid('forms:messages.maxCharacterLimit', {
            number: DashValidation.MaxNameLength.toString(),
          });
          return false;
        }
        break;
      }
      case FormFields.ExteriorSqft:
      case FormFields.InteriorSqft:
      case FormFields.Bedrooms:
      case FormFields.Bathrooms: {
        const { min, max } = value as { min: number; max?: number };
        if (isNil(min)) {
          setInvalid('forms:messages.fieldIsRequired', {
            fieldName: `${unitMap[fieldName]} (min)`,
          });
          return false;
        }
        if (
          !validatePrice(
            min || min === 0 ? +min : undefined,
            max || max === 0 ? +max : undefined
          )
        ) {
          setInvalid('forms:messages.defaultMinMaxValidationError');
          return false;
        }
        break;
      }
      case FormFields.Prices: {
        if (value) {
          const { min, max } = value as IPriceRange;
          if (!validatePrice(min, max)) {
            setInvalid('forms:messages.startingPriceMoreThanHighest');
            return false;
          }
        }
        resetPricesErrors();
        break;
      }
    }
    errorHandlers[fieldName](null);
    return true;
  };

  return validateFieldByName;
};

export default useValidation;
